import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import type { SnackbarData } from '../../models';

/**
 * Unified Snackbar Component
 *
 * Displays notifications for success, error, and warning states with appropriate
 * styling, icons, and animations.
 *
 * @example
 * // Usage via SnackbarService
 * snackbarService.showSuccess('Operation completed successfully');
 * snackbarService.showError('An error occurred');
 * snackbarService.showWarning('Please review the changes');
 */
@Component({
  selector: 'app-snackbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './snackbar.component.html',
  styleUrl: './snackbar.component.scss'
})
export class SnackbarComponent {
  data = inject<SnackbarData>(MAT_SNACK_BAR_DATA);
  snackBarRef = inject(MatSnackBarRef);

  /**
   * Get the appropriate icon based on snackbar type
   */
  get iconName(): string {
    switch (this.data.type) {
      case 'success':
        return 'check_circle';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  }

  /**
   * Get the CSS class for the snackbar type
   */
  get typeClass(): string {
    return this.data.type || 'success';
  }

  dismiss(): void {
    this.snackBarRef.dismiss();
  }
}
